import { useState } from "react";
import {
  Spinner,
  Modal,
  Button,
  Table,
  Pagination,
} from "react-bootstrap";
import { BsEye } from "react-icons/bs";
import { useQuery } from "@tanstack/react-query";
import { FiUsers } from "react-icons/fi";

import { InvestorProfileData } from "../Models/InvestorProfileData";
import { useGetInvestors } from "../../api/queries/MuslimInvest/useGetInvestors";
import { useGetMITerms } from "../../api/queries/MuslimInvest/useGetMITerms";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { Users } from "../../types/Types";
import {
  AdminDetailSection,
  buildInvestorProfileFields,
  buildInvestorTermsFields,
  buildPortalUserFields,
} from "./MuslimInvestAdminDetailSections";
import AdminPanelHeader from "./AdminPanelHeader";
import "../css/Admin.css";

const Investors = () => {
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isPending } = useGetInvestors({ pageSize, page });
  const investors = data?.investorProfile || [];

  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const [selectedInvestor, setSelectedInvestor] =
    useState<InvestorProfileData | null>(null);

  const userIdStr = selectedInvestor?.userId?.toString() ?? "";

  const { data: investorTermsList, isLoading: termsLoading } = useGetMITerms({
    userId: userIdStr,
    enabled: showModal && !!userIdStr,
  });

  const { data: portalUser, isLoading: portalUserLoading } = useQuery({
    queryKey: ["admin-investor-portal-user", userIdStr],
    enabled: showModal && !!userIdStr,
    queryFn: async () => {
      const res = await axiosInstance.get<Users>(
        getAPIURL(`/User/${userIdStr}?id=${userIdStr}`)
      );
      return res.data.user;
    },
  });

  const openModal = (investor: InvestorProfileData) => {
    setSelectedInvestor(investor);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedInvestor(null);
    setShowModal(false);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<FiUsers size={22} />}
        title="Investors"
        description="Manage investor profiles and onboarding."
      />

      {isPending && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {!isPending && investors.length === 0 && (
        <div className="alert alert-info text-center">No investors found</div>
      )}

      {!isPending && investors.length > 0 && (
        <>
          <div className="admin-data-table-wrap bg-white overflow-hidden">
            <Table hover responsive className="admin-data-table align-middle mb-0">
              <thead>
                <tr>
                  <th className="fw-semibold text-uppercase text-nowrap align-middle">
                    ID
                  </th>
                  <th className="fw-semibold text-uppercase text-nowrap align-middle">
                    User ID
                  </th>
                  <th className="fw-semibold text-uppercase text-nowrap align-middle">
                    Investment Type
                  </th>
                  <th className="fw-semibold text-uppercase text-nowrap align-middle">
                    Sectors of Interest
                  </th>
                  <th className="fw-semibold text-uppercase text-nowrap align-middle">
                    Stage
                  </th>
                  <th className="fw-semibold text-uppercase text-nowrap align-middle">
                    Status
                  </th>
                  <th className="fw-semibold text-uppercase text-nowrap align-middle text-end">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {investors.map((inv) => (
                  <tr key={inv.id}>
                    <td className="align-middle">
                      <span className="admin-table-code font-monospace fw-semibold text-nowrap">
                        {inv.id}
                      </span>
                    </td>
                    <td className="admin-table-muted align-middle text-nowrap">
                      {inv.userId ?? "—"}
                    </td>
                    <td className="align-middle fw-semibold">
                      {inv.investmentType || "—"}
                    </td>
                    <td className="align-middle">
                      <span
                        className="admin-table-desc"
                        title={inv.sectorsOfInterest || undefined}
                      >
                        {inv.sectorsOfInterest || "—"}
                      </span>
                    </td>
                    <td className="admin-table-muted align-middle text-nowrap">
                      {inv.developmentStage || "—"}
                    </td>
                    <td className="admin-table-muted align-middle text-nowrap">
                      {inv.status || "—"}
                    </td>
                    <td className="align-middle text-end text-nowrap">
                      <Button
                        size="sm"
                        variant="link"
                        type="button"
                        onClick={() => openModal(inv)}
                        className="admin-table-action-btn p-0 d-inline-flex align-items-center justify-content-center text-decoration-none"
                        aria-label="View investor details"
                      >
                        <BsEye size={15} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
              />
              {Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === page}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
              />
            </Pagination>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={closeModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Investor details</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {selectedInvestor && (
            <div className="p-2">
              <AdminDetailSection
                title="Portal member"
                fields={buildPortalUserFields(portalUser)}
                loading={portalUserLoading}
              />
              <AdminDetailSection
                title="Investor profile"
                fields={buildInvestorProfileFields(selectedInvestor)}
              />
              {termsLoading && (
                <AdminDetailSection
                  title="Investor terms & compliance"
                  fields={[]}
                  loading
                />
              )}
              {!termsLoading &&
                (investorTermsList?.length ? (
                  investorTermsList.map((terms, index) => (
                    <AdminDetailSection
                      key={terms.id ?? index}
                      title={
                        investorTermsList.length > 1
                          ? `Investor terms & compliance #${index + 1}`
                          : "Investor terms & compliance"
                      }
                      fields={buildInvestorTermsFields(terms)}
                    />
                  ))
                ) : (
                  <AdminDetailSection
                    title="Investor terms & compliance"
                    fields={[
                      {
                        label: "Terms",
                        value: "No investor terms on file",
                      },
                    ]}
                  />
                ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            className="rounded-pill"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Investors;
